import styled from "@emotion/styled";
import { useMemo, type ElementType, type ReactNode } from "react";

import { FONT_WEIGHT_SEMI_BOLD } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

// a small component that accepts a "format message" string and any values to be interpolated
// and emphasizes any content found between asterisks (no escaping supported at the moment)

// for example, the "Play", "walking", and "now" are emphasized in the following:
//
// content: `*'{action}* {modelName} *{animationName}'* animation *now*`
// values: { action: "Play", modelName: "robot", animationName: "walking" }
//
// this can be especially useful with multilingual messages where the word order may vary

const Text = styled.span``;

const EmphasizedText = styled.span`
  color: ${({ theme }) => theme.warning};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

type EmphasizedComponent = ElementType<{ children: ReactNode }>;

type Props = {
  content: string;
  values?: Record<string, unknown>;
  components?: {
    text?: EmphasizedComponent;
    emphasized?: EmphasizedComponent;
  };
};

export const EmphasizedMessage = ({ content, values = {}, components }: Props) => {
  const TextComponent = components?.text ?? Text;
  const EmphasizedComponent = components?.emphasized ?? EmphasizedText;

  const chunks = useMemo(() => content.split("*"), [content]);

  const formatStrings = useMemo(
    () => chunks.reduce((acc, chunk, index) => ({ ...acc, [index]: { en: chunk } }), {} as FormatMessages),
    [chunks],
  );

  const formatMessage = useFormatMessage(formatStrings);

  return useMemo(
    () =>
      chunks.map((chunk, index) => {
        if (chunk === "") return null;

        const key = `${chunk}-${index}`;
        const text = formatMessage(index, values);

        if (index % 2) {
          return <EmphasizedComponent key={key}>{text}</EmphasizedComponent>;
        }

        return <TextComponent key={key}>{text}</TextComponent>;
      }),
    [chunks, values, formatMessage, TextComponent, EmphasizedComponent],
  );
};
